import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import IRegisterForm from 'components/Customer/Interfaces/IRegisterForm';
import { getCaptchaToken } from 'components/GoogleRecaptcha/Index';
import FormKey from 'components/GeneralForm/Field/FormKey';
import FormList from 'automaailm-ui/lib/Components/FormList/index';
import FormListItem from 'components/Form/List/FormListItem';
import ControlPassword from 'components/GeneralForm/Field/ControlPassword';
import Button from 'automaailm-ui/lib/Components/Button';
import Buttons from 'automaailm-ui/lib/Components/Buttons';
import ControlInputWithoutClass from 'components/Form/Input/ControlInputWithoutClass';
import PlainCheckboxWithLabel from 'components/Form/Input/PlainCheckboxWithLabel';
import { getFormValidationRules } from 'components/Customer/Helpers/CustomerFormValidationHelper';
import CustomerCompanyForm from 'components/Customer/Form/CustomerCompanyForm';

interface IProps {
    config: IRegisterForm;
}

const CustomerRegisterForm: React.FunctionComponent<IProps> = (props) => {
    const { config } = props;
    const { passwordConfig } = config;
    const formElement = useRef<HTMLFormElement>(null);
    const [captchaToken, setCaptchaToken] = useState('');
    const FormDataValidationSchema = getFormValidationRules(config);

    const formik = useFormik({
        initialValues: config.formData,
        validationSchema: Yup.object().shape(FormDataValidationSchema),
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: () => {
            void getCaptchaToken().then((token) => {
                setCaptchaToken(token);

                if (formElement.current) {
                    formElement.current.submit();
                }
            });
        },
    });

    useEffect(() => {
        if (formik.values.is_company && !formik.values.country_id) {
            formik.setFieldValue('country_id', config.countrySelectConfig.options[0].value);
        }
    }, [formik.values.is_company]);

    return (
        <React.Fragment>
            <form
                className={'form create account form-create-account'}
                action={config.formConfig.actionUrl}
                method="post"
                ref={formElement}
                onSubmit={formik.handleSubmit}
            >
                <FormKey />
                {config.productUrl && (
                    <div>
                        <input name="productUrl" value={config.productUrl} hidden />
                    </div>
                )}
                <h2>{config.labels.registeringLabel}</h2>
                <h3>{config.labels.personalInformationLabel}</h3>
                <FormList>
                    <input type="hidden" name="success_url" value={config.formConfig.successUrl} />
                    <input type="hidden" name="error_url" value={config.formConfig.errorUrl} />
                    <input type="hidden" name="g-recaptcha-response" value={captchaToken} />
                    <FormListItem label={config.labels.firstNameLabel} required={true} error={formik.errors.firstname}>
                        <ControlInputWithoutClass
                            name="firstname"
                            value={formik.values.firstname ?? ''}
                            onChange={formik.handleChange}
                            readonly={config.nameConfig.isReadOnly}
                        />
                    </FormListItem>
                    <FormListItem label={config.labels.lastNameLabel} required={true} error={formik.errors.lastname}>
                        <ControlInputWithoutClass
                            name="lastname"
                            value={formik.values.lastname ?? ''}
                            onChange={formik.handleChange}
                            readonly={config.nameConfig.isReadOnly}
                        />
                    </FormListItem>
                    <FormListItem label={config.labels.emailLabel} required={true} error={formik.errors.email}>
                        <ControlInputWithoutClass
                            type="email"
                            name="email"
                            value={formik.values.email ?? ''}
                            onChange={formik.handleChange}
                        />
                    </FormListItem>

                    <FormListItem>
                        <PlainCheckboxWithLabel
                            label={config.labels.newsletterSignup}
                            checked={formik.values.is_subscribed === 1}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                formik.setFieldValue('is_subscribed', e.target.checked ? 1 : 0)
                            }
                        />
                        <input type="hidden" name="is_subscribed" value={formik.values.is_subscribed} />
                    </FormListItem>
                    <FormListItem>
                        <PlainCheckboxWithLabel
                            label={config.labels.registerCompanyLabel}
                            checked={formik.values.is_company === 1}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                formik.setFieldValue('is_company', e.target.checked ? 1 : 0)
                            }
                        />
                        <input type="hidden" name="is_company" value={formik.values.is_company} />
                    </FormListItem>

                    {formik.values.is_company === 1 && config.countrySelectConfig && config.regionSelectConfig && (
                        <CustomerCompanyForm
                            formik={formik}
                            labels={config.labels}
                            countrySelectConfig={config.countrySelectConfig}
                            regionSelectConfig={config.regionSelectConfig}
                        />
                    )}

                    <React.Fragment>
                        <h3>{config.labels.signInInformation}</h3>
                        <FormListItem
                            label={passwordConfig.labels.passwordLabel}
                            required={true}
                            error={formik.errors.password}
                        >
                            <ControlPassword
                                name="password"
                                value={formik.values.password ?? ''}
                                onChange={formik.handleChange}
                                showPasswordButtonLabel={passwordConfig.labels.showPasswordLabel}
                            />
                        </FormListItem>
                        <FormListItem
                            label={passwordConfig.labels.confirmPasswordLabel}
                            required={true}
                            error={formik.errors.password_confirmation}
                        >
                            <ControlPassword
                                name="password_confirmation"
                                value={formik.values.password_confirmation ?? ''}
                                onChange={formik.handleChange}
                                showPasswordButtonLabel={passwordConfig.labels.showPasswordLabel}
                            />
                        </FormListItem>
                    </React.Fragment>
                </FormList>

                <Buttons layout="vertical-wide">
                    <Button
                        type="submit"
                        intent="primary"
                        title={config.labels.formSubmitLabel}
                        disabled={formik.isSubmitting}
                    />
                    <Button
                        type="anchor"
                        intent="discrete"
                        title={config.labels.backLabel}
                        onClick={(e) => {
                            history.back();
                        }}
                    />
                </Buttons>
            </form>
        </React.Fragment>
    );
};

export default CustomerRegisterForm;
